import { isEmpty, isNil } from 'lodash';

import { findCustomValue } from '../customValuesUtils';
import { getTypedLanguage } from '../i18nutils';
import { EXACT_ONLINE_CLASS_NAME_FOR_CUSTOM_VALUE, EXACT_ONLINE_GL_ACCOUNTS_MATCHING } from '../integrations';
import { AccountLabel, AccountLabelCustomUsage, LeaseDTO, Language, Lease } from '../types';

import { ACCOUNT_CONTRACTOR_TOPCLASS, ACCOUNT_OWNERS_TOPCLASS } from './accountClasses';

export interface LeaseExactOnlineGLAccountsMatching {
  [accountLabelId: string]: {
    eoGLAccountId: string | null;
    exactOnlineCode: string | null;
  };
}

export const getFilteredAccountLabelsForContactOrDefault = (
  accountLabels: AccountLabel[],
  invoiceSenderOrRecipientId: string,
  clientContactId?: string | null
) =>
  getAccountLabelsFor(
    accountLabels,
    getOwnerIdForAccountLabelsFiltering(accountLabels, invoiceSenderOrRecipientId, clientContactId)
  );

export const getAccountLabelsFor = (accountLabels: AccountLabel[], ownerId?: string | null): AccountLabel[] => {
  const filteredAccountLabelsForOwner = accountLabels.filter(
    (accountLabel) => ownerId === accountLabel.ownerId || (!accountLabel.ownerId && !ownerId)
  );
  if (!isEmpty(filteredAccountLabelsForOwner)) {
    const accountLabelsContractor = accountLabels.filter(
      (accountLabel) => accountLabel.topClass === ACCOUNT_CONTRACTOR_TOPCLASS && !accountLabel.ownerId
    );
    return [...filteredAccountLabelsForOwner, ...accountLabelsContractor];
  }

  return accountLabels.filter((accountLabel) => !accountLabel.ownerId);
};

export const getOwnerIdForAccountLabelsFiltering = (
  accountLabels: AccountLabel[],
  invoiceSenderOrRecipientId: string,
  clientContactId?: string | null
) =>
  doesClientHasAccountLabelPerOwner(accountLabels) && invoiceSenderOrRecipientId !== clientContactId
    ? invoiceSenderOrRecipientId
    : null;

export const getAccountLabelClass = (accountLabel?: AccountLabel | null, lease?: Lease | LeaseDTO | null) => {
  if (!accountLabel) {
    return 0;
  }

  const accountingIntegrationValue = accountLabel.customValues?.find(
    (customValue) => customValue.name === EXACT_ONLINE_CLASS_NAME_FOR_CUSTOM_VALUE
  );
  if (accountingIntegrationValue) {
    const exactOnlineMatchingAtLeaseLevel: LeaseExactOnlineGLAccountsMatching = JSON.parse(
      findCustomValue(lease?.customValues, EXACT_ONLINE_GL_ACCOUNTS_MATCHING) ?? '{}'
    );
    return Number(
      exactOnlineMatchingAtLeaseLevel?.[accountLabel.id]?.exactOnlineCode ?? accountingIntegrationValue.value
    );
  }

  return accountLabel.class ?? 0;
};

export const getAccountLabelDescription = (accountLabel: AccountLabel | null | undefined, language: Language) =>
  accountLabel?.labels?.find((label) => getTypedLanguage(label.language) === getTypedLanguage(language))?.label ??
  accountLabel?.labels?.find((label) => getTypedLanguage(label.language) === Language.EN)?.label ??
  '';

export const sortAccountLabelsByDescription = (accountLabels: AccountLabel[], language: Language): AccountLabel[] =>
  [...accountLabels].sort((first, second) =>
    getAccountLabelDescription(first, language).localeCompare(getAccountLabelDescription(second, language), language, {
      sensitivity: 'base',
    })
  );

export const doesClientHasAccountLabelPerOwner = (accountLabels: AccountLabel[]): boolean =>
  accountLabels.some((accountLabel) => accountLabel.ownerId);

export const getAllOwnersOfAccountLabel = (accountLabels: AccountLabel[]) => {
  const uniqueOwnersSet = new Set<string>();
  accountLabels.forEach((label) => {
    if (label.ownerId) {
      uniqueOwnersSet.add(label.ownerId);
    }
  });
  return Array.from(uniqueOwnersSet);
};

export const getOwnerAccountLabelToWorkWith = (allAccountLabels: AccountLabel[], agency: boolean, ownerId: string) => {
  const accountLabelPerOwner = doesClientHasAccountLabelPerOwner(allAccountLabels);
  if (agency) {
    return allAccountLabels.find(
      (accountLabel) =>
        isNil(accountLabel.customUsage) &&
        (accountLabelPerOwner ? accountLabel.ownerId === ownerId : true) &&
        accountLabel.topClass === ACCOUNT_OWNERS_TOPCLASS
    );
  }
  return allAccountLabels.find(
    (accountLabel) =>
      accountLabel.customUsage === AccountLabelCustomUsage.OWNER_REQUEST_FOR_FUNDS &&
      (accountLabelPerOwner ? accountLabel.ownerId === ownerId : true) &&
      accountLabel.topClass === ACCOUNT_OWNERS_TOPCLASS
  );
};
